import { useNavigationDrawerValues } from 'components/navigation_drawer/navigation_drawer_store';
import { NavigationDrawerItemProps } from 'components/navigation_drawer/NavigationDrawerItem';
import {
    NAV_SECONDARY_ACTIVE_STYLE,
    NavigationItemTitle,
    SecondaryNavigationItemHeader,
    SecondaryNavigationItemsWrapper,
} from 'components/navigation_drawer/styled';
import { MediaSize, useIsViewportAtMost } from 'custom_hooks/useWindowSize';
import { memo, useEffect, useMemo, useRef } from 'react';

type SecondaryNavigationItemsProps = Readonly<{
    parentId: string;
    items: NavigationDrawerItemProps[] | undefined;
    show: boolean;
    fitToScreenHeight?: boolean;
}>;

function SecondaryNavigationItems({
    parentId,
    items,
    show,
    fitToScreenHeight,
}: SecondaryNavigationItemsProps) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const isSmallScreen = useIsViewportAtMost(MediaSize.small);

    const { setDrawerExpanded } = useNavigationDrawerValues((state) => ({
        setDrawerExpanded: state.setDrawerExpanded,
    }));

    const enabledItems = useMemo(() => items?.filter((item) => item.enabled) ?? [], [
        items,
    ]);

    const onSecondaryItemClick = (item: NavigationDrawerItemProps) => {
        item.onClick?.();
        if (isSmallScreen) {
            setDrawerExpanded(false);
        }
    };

    useEffect(() => {
        const boundingRect = wrapperRef.current?.getBoundingClientRect();
        if (fitToScreenHeight && wrapperRef.current) {
            wrapperRef.current.style.height =
                boundingRect && window.visualViewport
                    ? `${Math.min(
                          window.visualViewport.height - boundingRect.top,
                          boundingRect.height
                      )}px`
                    : 'unset';
        }
    }, [fitToScreenHeight]);

    return (
        <SecondaryNavigationItemsWrapper $show={show} $inPopup={fitToScreenHeight}>
            <div ref={wrapperRef} className="scrollable-y">
                {enabledItems.map((item) => (
                    <SecondaryNavigationItemHeader
                        id={`navigation-drawer-item-${parentId}-${item.id}`}
                        key={item.id}
                        to={item.route}
                        title={item.title}
                        exact={item.exact}
                        isActive={item.isActive}
                        activeStyle={NAV_SECONDARY_ACTIVE_STYLE}
                        onClick={() => onSecondaryItemClick(item)}
                    >
                        <NavigationItemTitle>{item.title}</NavigationItemTitle>
                    </SecondaryNavigationItemHeader>
                ))}
            </div>
        </SecondaryNavigationItemsWrapper>
    );
}

const memoSecondaryNavigationItems = memo(SecondaryNavigationItems);
export { memoSecondaryNavigationItems as SecondaryNavigationItems };
